import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { InformationSimple } from '@swyftx/aviary/icons/outlined';

import { MarginAccountHealthBar } from '../MarginAccountHealthBar';
import { MarginAccountHealthMarginDescription } from '../MarginAccountHealthMarginDescription';
import { MarginAccountHealthMarginEquityStopOut } from '../MarginAccountHealthMarginEquityStopOut';
import { MarginAccountHealthMarginEquityWarning } from '../MarginAccountHealthMarginEquityWarning';
import { MarginAccountHealthMarginLevel } from '../MarginAccountHealthMarginLevel';
import { MarginAccountHealthMarginStatus } from '../MarginAccountHealthMarginStatus';
import { MarginAccountHealthModal } from '../MarginAccountHealthModal';

type Props = {
  className?: string;
  stepIncrement?: number;
  maxIncrement?: number;
};

const MarginAccountHealth: React.FC<Props> = ({ className, stepIncrement, maxIncrement }) => {
  const { t } = useTranslation('margin');
  const isXs = useTailwindBreakpoint('xs');

  return (
    <FlexLayout direction='column' spacing={12} className={className}>
      <Card className='p-16 md:p-24'>
        <FlexLayout className='gap-16 md:gap-24' direction='column'>
          <FlexLayout alignItems='center' justifyContent='space-between'>
            <FlexLayout spacing={4} direction='column'>
              <Heading size={isXs ? 'h6' : 'h5'}>{t('accountHealth.title')}</Heading>
              <Body size='xsmall' color='secondary' className='hidden sm:block'>
                {t('accountHealth.subTitle')}
              </Body>
            </FlexLayout>
            <MarginAccountHealthModal stepIncrement={stepIncrement} maxIncrement={maxIncrement}>
              <Button
                tooltip={t('accountHealth.tooltip')}
                layout='icon'
                leadingIcon={<InformationSimple />}
                variant='outlined'
              />
            </MarginAccountHealthModal>
          </FlexLayout>

          <FlexLayout direction='column' className='w-full gap-16 md:gap-24'>
            <FlexLayout className='w-full flex-row gap-16 md:flex-col md:gap-24'>
              <FlexLayout justifyContent='start' className='w-full gap-16 md:gap-24'>
                <FlexLayout justifyContent='start' className='w-full flex-wrap gap-16 md:gap-24'>
                  <MarginAccountHealthMarginLevel
                    className='w-5/12'
                    size={isXs ? 'large' : 'xlarge'}
                    weight='emphasis'
                  />
                  <MarginAccountHealthMarginStatus
                    className='w-5/12'
                    size={isXs ? 'large' : 'xlarge'}
                    weight='emphasis'
                  />

                  <MarginAccountHealthMarginEquityWarning
                    direction='column'
                    className='flex w-5/12'
                    size='medium'
                    weight='emphasis'
                  />

                  <MarginAccountHealthMarginEquityStopOut
                    direction='column'
                    className='flex w-5/12'
                    size='medium'
                    weight='emphasis'
                  />
                </FlexLayout>
              </FlexLayout>

              <MarginAccountHealthMarginDescription />
            </FlexLayout>

            <MarginAccountHealthBar stepIncrement={stepIncrement} maxIncrement={maxIncrement} fontSize='xxsmall' />
          </FlexLayout>
        </FlexLayout>
      </Card>
    </FlexLayout>
  );
};

export { MarginAccountHealth };
